import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { PageHeader } from "./About";
import { useLang } from "../contexts/LanguageContext";
import { t, ACTIVITY_PHOTOS } from "../i18n/translations";
import useSEO from "../hooks/useSEO";

const PREVIEW_PHOTOS = [
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/3enovdmh_IMG_6267.JPG",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/hno8x6d0_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/n1q29uls_image.png",
  "https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/a15uc6qf_image.png",
];

const Workshops = () => {
  const { lang } = useLang();
  useSEO({
    title: lang === "en"
      ? "Our Work — Workshops & Programmes | Revelo"
      : "工作項目 — 工作坊及計劃 | Revelo",
    description: lang === "en"
      ? "Revelo runs interactive anti-scam workshops, school visits and community sessions across Hong Kong."
      : "Revelo 在香港舉辦互動式反詐騙工作坊、學校參訪及社區活動。",
    lang,
  });
  const tr = t[lang];
  const w = tr.ourWork;

  return (
    <div data-testid="ourwork-page">
      <PageHeader
        eyebrow={w.eyebrow}
        title={w.title}
        intro={w.subtitle}
        heroImage="/our-work-hero.png"
      />

      {/* IN ACTION PREVIEW — moved to top */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.28em] text-[#E9A229] font-bold">{lang === "en" ? "Community" : "社區"}</p>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] leading-[1.1]">{w.galleryTitle}</h2>
            <div className="mt-6 h-1 w-16 bg-[#E9A229]" />
            <p className="mt-6 text-slate-700 text-lg leading-relaxed">{w.gallerySubtitle}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {PREVIEW_PHOTOS.map((src, idx) => (
              <Reveal key={idx} delay={idx * 0.05}>
                <figure data-testid={`inaction-${idx}`} className="group img-parallax-wrap overflow-hidden">
                  <img src={src} alt="" className="img-parallax w-full h-56 lg:h-72 object-cover" />
                </figure>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <Link
              to="/gallery"
              data-testid="view-full-gallery"
              className="btn-pill inline-flex items-center gap-2 bg-[#163B71] hover:bg-[#0A192F] text-white px-7 py-3.5 rounded-full font-bold uppercase tracking-wide transition-all"
            >
              {w.galleryCta} <ArrowRight size={16} className="btn-arrow" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="py-20 lg:py-28 bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.28em] text-[#E9A229] font-bold">{lang === "en" ? "Process" : "流程"}</p>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] leading-[1.1]">{w.processTitle}</h2>
            <div className="mt-6 h-1 w-16 bg-[#E9A229]" />
            <p className="mt-6 text-slate-700 text-lg leading-relaxed">{w.processIntro}</p>
          </Reveal>
          <StaggerGroup className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {w.process.map((p, i) => (
              <StaggerItem key={i}>
                <div data-testid={`process-${i}`} className="border-t-4 border-[#163B71] pt-6 h-full">
                  <p className="font-mono text-xs text-[#E9A229] font-bold tracking-widest">0{i + 1}</p>
                  <h3 className="mt-3 font-heading font-bold text-xl text-[#0A192F] leading-snug">{p.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* FLEXIBLE FORMATS */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.28em] text-[#E9A229] font-bold">{lang === "en" ? "Formats" : "形式"}</p>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] leading-[1.1]">{w.formatsTitle}</h2>
            <div className="mt-6 h-1 w-16 bg-[#E9A229]" />
            <p className="mt-6 text-slate-700 text-lg leading-relaxed">{w.formatsIntro}</p>
          </Reveal>
          <StaggerGroup className="mt-14 grid md:grid-cols-3 gap-x-8 gap-y-10">
            {w.formats.map((f, i) => (
              <StaggerItem key={i}>
                <div data-testid={`format-${i}`} className="h-full bg-white border border-slate-200 p-8 transition-all hover:border-[#163B71]">
                  <h3 className="font-heading font-bold text-xl text-[#0A192F]">{f.title}</h3>
                  <div className="mt-4 h-0.5 w-10 bg-[#E9A229]" />
                  <p className="mt-4 text-slate-600 leading-relaxed">{f.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* FOCUS AREAS */}
      <section className="py-20 lg:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.28em] text-[#E9A229] font-bold">{lang === "en" ? "Topics" : "主題"}</p>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] leading-[1.1]">{w.focusTitle}</h2>
            <div className="mt-6 h-1 w-16 bg-[#E9A229]" />
          </Reveal>
          <StaggerGroup className="mt-12 flex flex-wrap gap-3">
            {w.focusTags.map((tag, i) => (
              <StaggerItem key={i}>
                <span data-testid={`focus-tag-${i}`} className="inline-flex items-center px-5 py-2.5 bg-white border-2 border-[#163B71]/15 text-[#163B71] font-medium hover:border-[#E9A229] hover:bg-[#E9A229]/5 transition-all">
                  {tag}
                </span>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#163B71] text-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <Reveal>
            <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
              {lang === "en" ? "Bring Revelo to your community" : "把 Revelo 帶入你的社群"}
            </h3>
            <p className="mt-4 text-slate-300 max-w-xl mx-auto">
              {lang === "en" ? "Tell us about your community and we'll design a workshop that fits." : "告訴我們你的社群需要，我們會為你設計合適的工作坊。"}
            </p>
            <Link
              to="/contact?interest=partnership"
              data-testid="ourwork-cta"
              className="btn-pill mt-7 inline-flex items-center gap-2 bg-[#E9A229] hover:bg-[#C7841B] text-white px-7 py-3.5 rounded-full font-bold uppercase tracking-wide transition-all"
            >
              {lang === "en" ? "Partner With Us" : "與我們合作"}
              <ArrowRight size={16} className="btn-arrow" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Workshops;
